import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { aiSearchResponseService } from "../services/aiSearchResponseService";
import { aiSearchResponseDisplayId } from "./AiSearchResponseCommonStore";
import { AiSearchResponse } from "../../../api/graphql";
import { aiSearchConversationDisplayId } from "../../aiSearchConversation/store/AiSearchConversationCommonStore";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { getAiSearchToolCallValues } from "../../aiSearchToolCall/store/AiSearchToolCallCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { CrudRows } from "../../common/types";
import { EvaluationRating } from "../../../api/graphql";
import { aiSearchConversationService } from "../../aiSearchConversation/services/aiSearchConversationService";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class AiSearchResponseViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = aiSearchResponseService;
  aiSearchResponseInstance: AiSearchResponse | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  conversationService = aiSearchConversationService;
  conversationSearchValue: string = "";
  conversationIdValue: string = "";
  conversationSelectedValue: { displayId: string; id: string } | null = null;
  queryValue: string = "";
  responseValue: string = "";
  sourcesValue: string = "{}";
  explanationValue: string = "{}";
  tokenUsageValue: string = "{}";
  createdAtValue: Date | null = null;
  ratingValue: EvaluationRating | "" = "" as EvaluationRating | "";
  evaluatedAtValue: Date | null = null;
  evaluatedByService = userService;
  evaluatedBySearchValue: string = "";
  evaluatedByIdValue: string = "";
  evaluatedBySelectedValue: { displayId: string; id: string } | null = null;
  toolCallsColumnWidths: Record<string, number | string> = {};
  toolCallsRows: CrudRows = {};

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.toolCallsColumnWidths = {
      sequence: 200,
      toolName: 200,
      pattern: 200,
      durationMs: 200,
      success: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAiSearchResponseById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.aiSearchResponseOne({ id });
    this.aiSearchResponseInstance = result.aiSearchResponseOne;
    if (this.aiSearchResponseInstance) {
      this.loadValues(this.aiSearchResponseInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.aiSearchResponseInstance) {
      return aiSearchResponseDisplayId(
        this.aiSearchResponseInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AiSearchResponseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "conversation") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.aiSearchConversationOne({
          id: params.returnData.resultId,
        });
        const aiSearchConversation = result.aiSearchConversationOne;
        this.conversationSelectedValue = aiSearchConversation;
      } else if (params.callerData?.fieldName === "evaluatedBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.evaluatedBySelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get conversationValue() {
    if (this.conversationSelectedValue) {
      return {
        id: this.conversationSelectedValue.id,
        displayId: aiSearchConversationDisplayId(
          this.conversationSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  getRatingOptions() {
    return this.langStore.EvaluationRatingOptions;
  }

  get evaluatedByValue() {
    if (this.evaluatedBySelectedValue) {
      return {
        id: this.evaluatedBySelectedValue.id,
        displayId: userDisplayId(this.evaluatedBySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get toolCallsVisibleColumns() {
    return [
      {
        name: "sequence",
        title: this.langStore.AiSearchToolCallSequenceLabel,
        width: this.toolCallsColumnWidths["sequence"],
      },
      {
        name: "toolName",
        title: this.langStore.AiSearchToolCallToolNameLabel,
        width: this.toolCallsColumnWidths["toolName"],
      },
      {
        name: "pattern",
        title: this.langStore.AiSearchToolCallPatternLabel,
        width: this.toolCallsColumnWidths["pattern"],
      },
      {
        name: "durationMs",
        title: this.langStore.AiSearchToolCallDurationMsLabel,
        width: this.toolCallsColumnWidths["durationMs"],
      },
      {
        name: "success",
        title: this.langStore.AiSearchToolCallSuccessLabel,
        width: this.toolCallsColumnWidths["success"],
      },
    ];
  }

  toolCallsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.toolCallsColumnWidths = values;
  }

  get toolCallsRowsDyn() {
    return this.toolCallsRows;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.aiSearchResponseInstance = null;
    this.conversationSearchValue = "";
    this.conversationIdValue = "";
    this.queryValue = "";
    this.responseValue = "";
    this.sourcesValue = "{}";
    this.explanationValue = "{}";
    this.tokenUsageValue = "{}";
    this.createdAtValue = null;
    this.ratingValue = "" as EvaluationRating | "";
    this.evaluatedAtValue = null;
    this.evaluatedBySearchValue = "";
    this.evaluatedByIdValue = "";
    this.toolCallsRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAiSearchResponse() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editAiSearchResponse() {
    const id = this.aiSearchResponseInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/aiSearchResponse/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAiSearchResponse(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAiSearchResponse(): any {
    yield this.close();
  }

  loadValues(instance: AiSearchResponse) {
    this.idValue = instance.id;
    this.conversationSelectedValue = instance.conversation;
    this.queryValue = instance.query;
    this.responseValue = instance.response;
    this.sourcesValue = instance.sources || "";
    this.explanationValue = instance.explanation || "";
    this.tokenUsageValue = instance.tokenUsage || "";
    this.createdAtValue = new Date(instance.createdAt);
    this.ratingValue = instance.rating || ("" as EvaluationRating | "");
    this.evaluatedAtValue = instance.evaluatedAt
      ? new Date(instance.evaluatedAt)
      : null;
    this.evaluatedBySelectedValue = instance.evaluatedBy
      ? instance.evaluatedBy
      : null;
    this.toolCallsRows = instance.toolCalls
      ? Object.assign(
          {},
          ...instance.toolCalls.map((x) => {
            const processedRow = getAiSearchToolCallValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAiSearchResponseById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
